import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Database, Robot, Storefront, WhatsappLogo, Car,
  CheckCircle, WarningCircle, XCircle, Question, ArrowClockwise, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import LlmCostWidget from "@/components/LlmCostWidget";
import BotTestModeCard from "@/components/BotTestModeCard";
import { useAuth } from "@/contexts/AuthContext";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const COMPONENT_META = {
  cms: {
    labelEs: "CMS · Consulta de Chasis",
    labelEn: "CMS · Chassis Lookup",
    icon: Database,
    descEs: "Middleware NestJS de TI — lookup por Cédula/RNC/Pasaporte",
    descEn: "IT NestJS middleware — Cedula/RNC/Passport lookup",
  },
  whatsapp: {
    labelEs: "WhatsApp Business",
    labelEn: "WhatsApp Business",
    icon: WhatsappLogo,
    descEs: "Meta Cloud API — validación de token y phone number",
    descEn: "Meta Cloud API — token and phone number validation",
  },
  mongodb: {
    labelEs: "Base de Datos (MongoDB)",
    labelEn: "Database (MongoDB)",
    icon: Storefront,
    descEs: "Ping y disponibilidad de colecciones",
    descEn: "Ping and collection availability",
  },
  emergent_llm: {
    labelEs: "LLM · Emergent Key",
    labelEn: "LLM · Emergent Key",
    icon: Robot,
    descEs: "Claude/GPT proxy — verifica saldo y disponibilidad",
    descEn: "Claude/GPT proxy — verifies budget and availability",
  },
  scraper: {
    labelEs: "Scraper de Inventario",
    labelEn: "Inventory Scraper",
    icon: Car,
    descEs: "Frescura de sync de vehículos nuevos y seminuevos",
    descEn: "Freshness of new/used vehicles sync",
  },
};

const STATUS_META = {
  up: { color: "emerald", labelEs: "Operativo", labelEn: "Up", icon: CheckCircle },
  degraded: { color: "amber", labelEs: "Degradado", labelEn: "Degraded", icon: WarningCircle },
  down: { color: "red", labelEs: "Caído", labelEn: "Down", icon: XCircle },
  unknown: { color: "slate", labelEs: "Sin datos", labelEn: "Unknown", icon: Question },
};

const STATUS_VALUE = { up: 100, degraded: 60, unknown: 30, down: 0 };

function relativeTime(iso, lang) {
  if (!iso) return lang === "es" ? "—" : "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return lang === "es" ? "hace unos segundos" : "just now";
    if (diff < 3600) return lang === "es" ? `hace ${Math.floor(diff / 60)} min` : `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return lang === "es" ? `hace ${Math.floor(diff / 3600)} h` : `${Math.floor(diff / 3600)} h ago`;
    return d.toLocaleString();
  } catch { return iso; }
}

function StatusPill({ status, lang }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const Icon = meta.icon;
  const label = lang === "es" ? meta.labelEs : meta.labelEn;
  const cls = {
    emerald: "bg-emerald-500/10 text-emerald-600 border-emerald-500/40",
    amber: "bg-amber-500/10 text-amber-600 border-amber-500/40",
    red: "bg-red-500/10 text-red-600 border-red-500/40",
    slate: "bg-slate-500/10 text-slate-500 border-slate-500/40",
  }[meta.color];
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-[0.1em] ${cls}`}>
      <Icon size={11} weight="fill" />
      {label}
    </div>
  );
}

function ComponentCard({ componentKey, snapshot, lang, onRecheck, busy }) {
  const meta = COMPONENT_META[componentKey];
  const Icon = meta.icon;
  const label = lang === "es" ? meta.labelEs : meta.labelEn;
  const desc = lang === "es" ? meta.descEs : meta.descEn;
  const status = snapshot?.status || "unknown";
  const latency = snapshot?.latency_ms;
  const error = snapshot?.error;
  const created = snapshot?.created_at;
  const [history, setHistory] = useState([]);
  const [hLoading, setHLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setHLoading(true);
    api.get(`/health/history?component=${componentKey}&hours=24`)
      .then((r) => {
        if (!mounted) return;
        const rows = (r.data || []).map((d) => ({
          t: new Date(d.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          value: STATUS_VALUE[d.status] ?? 0,
          status: d.status,
          latency_ms: d.latency_ms,
        }));
        setHistory(rows);
      })
      .catch(() => {})
      .finally(() => mounted && setHLoading(false));
    return () => { mounted = false; };
  }, [componentKey, snapshot?.id]);

  return (
    <div
      className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3"
      data-testid={`health-card-${componentKey}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={18} weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm truncate">{label}</div>
            <div className="text-[11px] text-muted-foreground line-clamp-2">{desc}</div>
          </div>
        </div>
        <StatusPill status={status} lang={lang} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">
            {lang === "es" ? "Latencia" : "Latency"}
          </div>
          <div className="font-mono font-semibold" data-testid={`health-latency-${componentKey}`}>
            {latency != null ? `${latency} ms` : "—"}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">
            {lang === "es" ? "Última verificación" : "Last check"}
          </div>
          <div className="font-mono font-semibold flex items-center gap-1">
            <Clock size={10} /> {relativeTime(created, lang)}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="text-[11px] bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/30 rounded p-2 whitespace-pre-wrap break-words"
          data-testid={`health-error-${componentKey}`}
        >
          {error}
        </div>
      )}

      {/* Timeline */}
      <div className="mt-1">
        <div className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-1">
          {lang === "es" ? "Últimas 24 h" : "Last 24 h"}
        </div>
        <div className="h-[70px]">
          {hLoading ? (
            <div className="h-full flex items-center justify-center text-[10px] text-muted-foreground">
              {lang === "es" ? "Cargando…" : "Loading…"}
            </div>
          ) : history.length === 0 ? (
            <div className="h-full flex items-center justify-center text-[10px] text-muted-foreground">
              {lang === "es" ? "Sin historial aún" : "No history yet"}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 3" strokeOpacity={0.2} />
                <XAxis dataKey="t" tick={{ fontSize: 8 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} ticks={[0, 60, 100]} tick={{ fontSize: 8 }} />
                <Tooltip
                  contentStyle={{ fontSize: 10 }}
                  formatter={(_, __, p) => [`${p.payload.status} (${p.payload.latency_ms ?? "—"} ms)`, ""]}
                  labelFormatter={(l) => l}
                />
                <Line
                  type="stepAfter" dataKey="value"
                  stroke="#0052CC" strokeWidth={2} dot={false} isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          size="sm" variant="outline" onClick={() => onRecheck(componentKey)}
          disabled={busy === componentKey}
          data-testid={`health-recheck-${componentKey}`}
        >
          <ArrowClockwise size={12} className={`mr-1 ${busy === componentKey ? "animate-spin" : ""}`} />
          {lang === "es" ? "Re-chequear" : "Re-check"}
        </Button>
      </div>
    </div>
  );
}

function LlmCostSection() {
  const { user } = useAuth();
  const [cost, setCost] = useState(null);
  const load = useCallback(() => api.get("/analytics/llm-usage/status").then((r) => setCost(r.data)).catch(() => {}), []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role, load]);
  if (user?.role !== "admin" || !cost) return null;
  return (
    <LlmCostWidget cost={cost} onCapUpdate={async (newCap) => {
      try {
        await api.put("/analytics/llm-usage/cap", { cap_usd: newCap });
        await load();
        toast.success(`Cap actualizado a $${newCap}`);
      } catch {
        toast.error("Error actualizando cap");
      }
    }} />
  );
}

export default function BotHealthPanel({ lang }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [globalBot, setGlobalBot] = useState(null);
  const [gbBusy, setGbBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snap, gb] = await Promise.all([
        api.get("/health/status"),
        api.get("/settings/bot-global").catch(() => ({ data: { enabled: true } })),
      ]);
      setSnapshot(snap.data);
      setGlobalBot(gb.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error(lang === "es" ? "Sin permiso para ver el estado del bot" : "No permission to view bot health");
      }
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const toggleGlobalBot = async () => {
    const newState = !(globalBot?.enabled);
    if (!newState) {
      const reason = window.prompt(
        lang === "es"
          ? "¿Por qué apagas el bot? (opcional, verá el equipo)"
          : "Why disabling the bot? (optional, visible to team)"
      );
      if (reason === null) return;  // user cancelled
      setGbBusy(true);
      try {
        await api.patch("/settings/bot-global", { enabled: false, reason: reason || null });
        toast.warning(lang === "es"
          ? "🔴 Bot apagado globalmente — todas las conversaciones las atenderán humanos"
          : "🔴 Bot disabled globally — all conversations will be attended by humans");
        await load();
      } catch {
        toast.error(lang === "es" ? "Error al apagar el bot" : "Failed to disable bot");
      } finally {
        setGbBusy(false);
      }
    } else {
      setGbBusy(true);
      try {
        await api.patch("/settings/bot-global", { enabled: true });
        toast.success(lang === "es"
          ? "✅ Bot activado globalmente — retomando respuestas automáticas"
          : "✅ Bot enabled globally — resuming automatic replies");
        await load();
      } catch {
        toast.error(lang === "es" ? "Error al activar el bot" : "Failed to enable bot");
      } finally {
        setGbBusy(false);
      }
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh snapshot every 60s (probes still run hourly server-side).
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const recheckOne = async (component) => {
    setBusy(component);
    try {
      await api.post(`/health/run?component=${component}`);
      toast.success(lang === "es" ? "Verificación completa" : "Check complete");
      await load();
    } catch {
      toast.error(lang === "es" ? "Error al ejecutar el chequeo" : "Failed to run check");
    } finally {
      setBusy(null);
    }
  };

  const recheckAll = async () => {
    setBusy("__all__");
    try {
      await api.post("/health/run");
      toast.success(lang === "es" ? "Todos los componentes re-chequeados" : "All components re-checked");
      await load();
    } catch {
      toast.error(lang === "es" ? "Error al ejecutar los chequeos" : "Failed to run checks");
    } finally {
      setBusy(null);
    }
  };

  const overall = snapshot?.overall || "unknown";
  const components = snapshot?.components || {};
  const overallMeta = STATUS_META[overall];
  const OverallIcon = overallMeta.icon;

  return (
    <div className="space-y-4" data-testid="bot-health-panel">
      {/* Global bot kill-switch (admin only — Sprint 30A) */}
      {globalBot && (
        <div
          className={`rounded-lg border p-4 flex items-center justify-between gap-3 ${
            globalBot.enabled
              ? "bg-emerald-500/5 border-emerald-500/30"
              : "bg-red-500/8 border-red-500/40 shadow-[0_0_0_2px_rgba(239,68,68,0.15)]"
          }`}
          data-testid="global-bot-toggle-card"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              globalBot.enabled ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"
            }`}>
              <Robot size={22} weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm">
                {globalBot.enabled
                  ? (lang === "es" ? "Bot Concierge · Activo (responde automáticamente)" : "Bot Concierge · Active (auto-replies)")
                  : (lang === "es" ? "Bot Concierge · APAGADO (solo humanos)" : "Bot Concierge · OFF (humans only)")}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {globalBot.enabled
                  ? (lang === "es"
                      ? "Todas las conversaciones nuevas son atendidas por el AI. Los agentes toman el control manualmente cuando lo necesiten."
                      : "All new conversations are handled by AI. Agents take over manually when needed.")
                  : (lang === "es"
                      ? `Apagado ${globalBot.disabled_at ? relativeTime(globalBot.disabled_at, lang) : ""}${globalBot.reason ? ` · Motivo: ${globalBot.reason}` : ""} · Solo agentes humanos responden. El bot no volverá a activarse hasta que lo enciendas.`
                      : `Disabled${globalBot.reason ? ` · Reason: ${globalBot.reason}` : ""} · Humans only. Bot stays off until you re-enable.`)}
              </div>
            </div>
          </div>
          <Button
            onClick={toggleGlobalBot}
            disabled={gbBusy}
            variant={globalBot.enabled ? "outline" : "default"}
            className={!globalBot.enabled ? "bg-emerald-500 hover:bg-emerald-600 text-white" : ""}
            data-testid="global-bot-toggle-btn"
          >
            {gbBusy
              ? "…"
              : globalBot.enabled
                ? (lang === "es" ? "Apagar bot globalmente" : "Disable bot globally")
                : (lang === "es" ? "Encender bot" : "Enable bot")}
          </Button>
        </div>
      )}

      <BotTestModeCard />

      {/* Overall banner */}
      <div
        className={`rounded-lg border p-4 flex items-center justify-between gap-3 ${
          overall === "up" ? "bg-emerald-500/5 border-emerald-500/30"
          : overall === "degraded" ? "bg-amber-500/5 border-amber-500/30"
          : overall === "down" ? "bg-red-500/5 border-red-500/30"
          : "bg-slate-500/5 border-slate-500/30"
        }`}
        data-testid="health-overall-banner"
      >
        <div className="flex items-center gap-3">
          <OverallIcon
            size={28} weight="fill"
            className={
              overall === "up" ? "text-emerald-500"
              : overall === "degraded" ? "text-amber-500"
              : overall === "down" ? "text-red-500"
              : "text-slate-400"
            }
          />
          <div>
            <div className="font-heading font-bold text-lg leading-tight">
              {lang === "es" ? "Salud del bot" : "Bot health"}
              {" · "}
              <span data-testid="health-overall-status">
                {overall === "up" ? (lang === "es" ? "Todo operativo" : "All systems operational")
                : overall === "degraded" ? (lang === "es" ? "Servicio degradado" : "Degraded service")
                : overall === "down" ? (lang === "es" ? "Componente crítico caído" : "Critical component down")
                : (lang === "es" ? "Sin datos suficientes" : "Not enough data")}
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {lang === "es"
                ? `Snapshot generado: ${relativeTime(snapshot?.generated_at, lang)} · Chequeos automáticos cada hora`
                : `Snapshot generated: ${relativeTime(snapshot?.generated_at, lang)} · Auto-checks every hour`}
            </div>
          </div>
        </div>
        <Button
          onClick={recheckAll} disabled={busy !== null}
          data-testid="health-recheck-all"
        >
          <ArrowClockwise size={14} className={`mr-1.5 ${busy === "__all__" ? "animate-spin" : ""}`} />
          {lang === "es" ? "Re-chequear todo" : "Re-check all"}
        </Button>
      </div>

      {loading && !snapshot ? (
        <div className="p-12 text-center text-sm text-muted-foreground">
          {lang === "es" ? "Cargando estado del bot…" : "Loading bot health…"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Object.keys(COMPONENT_META).map((k) => (
            <ComponentCard
              key={k}
              componentKey={k}
              snapshot={components[k]}
              lang={lang}
              onRecheck={recheckOne}
              busy={busy}
            />
          ))}
        </div>
      )}
      <LlmCostSection />
    </div>
  );
}
